import React, { useState, useContext } from 'react';
import Collapse from 'react-bootstrap/Collapse';
import PropTypes from 'prop-types';
import OrganizationSearchContext from '../../context/organization_search.context';
import TypeaheadSelectBootstrap5 from '../../../components/react/autocompletes/typeahead_select_bootstrap_5';
import Checkbox from '../../../components/react/checkbox/checkbox';
import AutocompleteBootstrap5 from '../../../components/react/autocompletes/autocomplete_bootstrap_5';
import { autocompletePlacesPath } from '../../../utilities/api_relative_paths';
import { Range } from '../../../components/react/slider/slider';
import {
  SLIDER_STEPS,
  ORG_SIZE_DEFAULT_MIN,
  ORG_SIZE_DEFAULT_MAX,
} from '../../constants/organization_search.constants';

const SidebarFilters = ({
  isLoading,
  types,
  setTypes,
  setPageNumber,
  hiringNowInput,
  setHiringNowInput,
  officeLocations,
  setOfficeLocations,
  topEmployersInput,
  setTopEmployersInput,
  hiringLocations,
  setHiringLocations,
  orgSizeMin,
  setOrgSizeMin,
  orgSizeMax,
  setOrgSizeMax,
  sliderArrayInput,
  setSliderArrayInput,
  clearAllFilters,
}) => {
  const { isDesktop, organizationTypes } = useContext(OrganizationSearchContext);
  const [open, setOpen] = useState(isDesktop);

  /**
   * @param {Array} values - Array of 2 values, respectively min and max
   * @returns {undefined}
   */
  const onSliderChange = (values) => {
    const newMinValue = values[0];
    const newMaxValue = values[1];
    setOrgSizeMin(SLIDER_STEPS[newMinValue]);
    setOrgSizeMax(SLIDER_STEPS[newMaxValue]);
  };

  const onHiringNowChange = () => {
    setHiringNowInput(!hiringNowInput);
    setPageNumber(1);
  };

  const onTopEmployersChange = () => {
    setTopEmployersInput(!topEmployersInput);
    setPageNumber(1);
  };

  /**
   * @type {Node} - Node showing the mobile filters' section
   */
  const renderDropdownButton = (
    <div className='d-flex justify-content-center'>
      <button
        // eslint-disable-next-line max-len
        className='mt-35 btn btn-line-secondary btn-block on-gray-bg w-100 mobile-sidebar-filters-collapse-button'
        aria-controls='filter-sidebar'
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className='text-blue'>
          <i className='fal icon-sliders-h' />
          <span className='mx-15'>Filters</span>
          <i className={`fal icon-angle-${open ? 'up' : 'down'}`} />
        </span>
      </button>
    </div>
  );

  /**
   * TODO: add real filters
   *
   * @type {Node} Node showing desktop filters' section
   */
  const renderSidebarFilters = (
    <Collapse in={open}>
      <div id='filter-sidebar' className='filter-sidebar'>
        <fieldset
          aria-label='Organization search sidebar filters'
          disabled={isLoading}
          className='pt-35'
        >
          <div className='form-group mb-35 pb-15'>
            <div className='slider-container me-35 mt-0'>
              <span id='topEmployersCheckbox'>
                <Checkbox
                  dataTestId='topEmployersCheckbox'
                  isSlider={true}
                  isToggled={topEmployersInput}
                  disabled={isLoading}
                  handleChange={onTopEmployersChange}
                />
              </span>
              <label htmlFor='topEmployersCheckbox' className='text-gray ms-25'>
                Top Employers
              </label>
            </div>
          </div>
          <div className='form-group mb-35'>
            <label htmlFor='organization-type' className='pb-15'>
              Organization type
            </label>
            <TypeaheadSelectBootstrap5
              id='organization-type'
              inputProps={{
                'aria-label': 'organization-type-filter',
                className: 'form-control',
              }}
              multiple={true}
              placeholder='e.g. "Foundation"...'
              onChange={(options) => {
                setPageNumber(1);
                setTypes(options);
              }}
              options={organizationTypes}
              selected={organizationTypes.filter((entry) => types.includes(entry))}
            />
          </div>
          <div className='form-group mb-35'>
            <label htmlFor='office-locations' className='pb-15'>
              Location
            </label>
            <AutocompleteBootstrap5
              id='office-locations-autocomplete'
              apiPath={autocompletePlacesPath}
              multiple={true}
              filter={{ 'types[]': ['country', 'region'] }}
              inputProps={{
                'aria-label': 'office-locations',
                className: 'form-control',
              }}
              arrayFormat={'repeat'}
              placeholder='e.g. "Namibia"...'
              onSelect={(selectedOptions) => {
                setPageNumber(1);
                setOfficeLocations(selectedOptions.map((entry) => ({
                  place_key: entry.id,
                  name: entry.text,
                })));
              }}
              selected={officeLocations.map((location) => ({
                id: location.place_key,
                text: location.name,
              }))}
            />
          </div>

          <div className='form-group mb-35'>
            <label htmlFor='organization-size'>Organization size</label>
            <div className='px-15'>
              <Range
                className='mt-35'
                allowCross={false}
                min={ORG_SIZE_DEFAULT_MIN.value}
                max={ORG_SIZE_DEFAULT_MAX.value}
                onChange={(newArray) => setSliderArrayInput(newArray)}
                onAfterChange={onSliderChange}
                disabled={isLoading}
                value={sliderArrayInput}
                ariaLabelGroupForHandles={[
                  'organization-minimum-size',
                  'organization-maximum-size',
                ]}
              />
            </div>
            <div className='mt-15 text-gray-li small'>
              {orgSizeMin}
              {orgSizeMin === orgSizeMax ? ' ' : ` - ${orgSizeMax} `}
              employee{orgSizeMax === '1' ? '' : 's'}
            </div>
          </div>

          <div className='form-group mb-35'>
            <label htmlFor='hiring-locations' className='pb-15'>
              Recruits in this location
            </label>
            <AutocompleteBootstrap5
              id='hiring-locations-autocomplete'
              apiPath={autocompletePlacesPath}
              filterBy={(option) => hiringLocations.every((element) => element !== option.text)}
              multiple={true}
              inputProps={{
                'aria-label': 'hiring-locations',
                className: 'form-control',
              }}
              onSelect={(selectedOptions) => {
                setPageNumber(1);
                setHiringLocations(selectedOptions.map((item) => item.text || item));
              }}
              placeholder='e.g. "Chile" or "Madrid"...'
              selected={hiringLocations}
            />
          </div>
          <div className='form-group mb-35 pb-25'>
            <div className='slider-container me-35'>
              <span id='hiringNowCheckbox'>
                <Checkbox
                  dataTestId='hiringNowCheckbox'
                  isSlider={true}
                  isToggled={hiringNowInput}
                  disabled={isLoading}
                  handleChange={onHiringNowChange}
                />
              </span>
              <label htmlFor='hiringNowCheckbox' className='slider-label text-gray ms-25'>
                Currently hiring
              </label>
            </div>
          </div>
          <div className='form-group mb-35 mb-sm-48'>
            <button type='button' className='btn btn-outline-secondary' onClick={clearAllFilters}>
              <i className='icon icon-times-circle fal pe-15' />
              Clear all filters
            </button>
          </div>
        </fieldset>
      </div>
    </Collapse>
  );

  return (
    <div className='col-xs-12 col-lg-4'>
      {!isDesktop && renderDropdownButton}
      {renderSidebarFilters}
    </div>
  );
};

SidebarFilters.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  types: PropTypes.array.isRequired,
  setTypes: PropTypes.func.isRequired,
  setPageNumber: PropTypes.func.isRequired,
  hiringNowInput: PropTypes.bool.isRequired,
  setHiringNowInput: PropTypes.func.isRequired,
  topEmployersInput: PropTypes.bool.isRequired,
  setTopEmployersInput: PropTypes.func.isRequired,
  hiringLocations: PropTypes.array.isRequired,
  setHiringLocations: PropTypes.func.isRequired,
  officeLocations: PropTypes.array.isRequired,
  setOfficeLocations: PropTypes.func.isRequired,
  orgSizeMin: PropTypes.string.isRequired,
  setOrgSizeMin: PropTypes.func.isRequired,
  orgSizeMax: PropTypes.string.isRequired,
  setOrgSizeMax: PropTypes.func.isRequired,
  sliderArrayInput: PropTypes.array.isRequired,
  setSliderArrayInput: PropTypes.func.isRequired,
  clearAllFilters: PropTypes.func.isRequired,
};

export default SidebarFilters;
